// LocalStorage-backed chat UI preferences. Kept out of the chat domain
// store so the store stays focused on session/message state.

import { IS_ADMIN_MODE } from '../hooks/useIsAdminMode'
import type { SessionFilter } from '../stores/chat/types'

const DIAGNOSTICS_KEY = 'chat_diagnostics_enabled'
const SIDEBAR_WIDTH_KEY = 'chat_sidebar_width'
const SIDEBAR_COLLAPSED_KEY_PREFIX = 'chat_sidebar_collapsed:'
const SESSION_FILTER_KEY = 'chat_session_filter'
const CHAT_INTRO_DISMISSED_KEY_PREFIX = 'chat_intro_dismissed:'

const SESSION_FILTERS: readonly SessionFilter[] = ['mine', 'team', 'all']
export const DEFAULT_SESSION_FILTER: SessionFilter = 'mine'

export const SIDEBAR_MIN_WIDTH = 320
export const SIDEBAR_MAX_WIDTH = 720
export const SIDEBAR_DEFAULT_WIDTH = 400
export const SIDEBAR_COLLAPSED_WIDTH = 56

export function clampSidebarWidth(value: number): number {
  if (Number.isNaN(value)) return SIDEBAR_DEFAULT_WIDTH
  if (value < SIDEBAR_MIN_WIDTH) return SIDEBAR_MIN_WIDTH
  if (value > SIDEBAR_MAX_WIDTH) return SIDEBAR_MAX_WIDTH
  return value
}

export function loadDiagnosticsEnabled(): boolean {
  if (typeof window === 'undefined' || !IS_ADMIN_MODE) return false
  const stored = window.localStorage.getItem(DIAGNOSTICS_KEY)
  return stored === null || stored === '1'
}

export function persistDiagnosticsEnabled(enabled: boolean) {
  if (typeof window === 'undefined') return
  if (enabled) {
    window.localStorage.setItem(DIAGNOSTICS_KEY, '1')
  } else {
    // Persist an explicit "off" (rather than clearing) so it survives the
    // non-production default-on above.
    window.localStorage.setItem(DIAGNOSTICS_KEY, '0')
  }
}

// The shared-chat list defaults to "Mine" so each user lands on their own
// threads, with Team and All one click away.
export function loadSessionFilter(): SessionFilter {
  if (typeof window === 'undefined') return DEFAULT_SESSION_FILTER
  const stored = window.localStorage.getItem(SESSION_FILTER_KEY)
  return SESSION_FILTERS.includes(stored as SessionFilter)
    ? (stored as SessionFilter)
    : DEFAULT_SESSION_FILTER
}

export function persistSessionFilter(filter: SessionFilter) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(SESSION_FILTER_KEY, filter)
}

// The one-time "New! Try chat" nudge. Dismissed state is kept per user (keyed
// by Supabase user id) so each person sees it once on their own account, even
// when they share a browser. Defaults to dismissed (hidden) until proven
// otherwise, so a brief unhydrated render never flashes the badge.
function chatIntroDismissedKey(userId: string | null): string {
  return `${CHAT_INTRO_DISMISSED_KEY_PREFIX}${userId ?? 'anon'}`
}

export function loadChatIntroDismissed(userId: string | null): boolean {
  if (typeof window === 'undefined') return true
  return window.localStorage.getItem(chatIntroDismissedKey(userId)) === '1'
}

export function persistChatIntroDismissed(userId: string | null) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(chatIntroDismissedKey(userId), '1')
}

export function loadSidebarWidth(): number {
  if (typeof window === 'undefined') return SIDEBAR_DEFAULT_WIDTH
  const stored = window.localStorage.getItem(SIDEBAR_WIDTH_KEY)
  if (stored === null) return SIDEBAR_DEFAULT_WIDTH
  return clampSidebarWidth(Number.parseInt(stored, 10))
}

export function persistSidebarWidth(width: number) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(
    SIDEBAR_WIDTH_KEY,
    String(clampSidebarWidth(width)),
  )
}

// Keyed by project slug so an open chat in one project never carries into
// another. localStorage so a new tab opens the panel the way it was left.
function sidebarCollapsedKey(projectSlug: string): string {
  return `${SIDEBAR_COLLAPSED_KEY_PREFIX}${projectSlug}`
}

export function loadSidebarCollapsed(projectSlug: string): boolean {
  if (typeof window === 'undefined') return true
  const stored = window.localStorage.getItem(sidebarCollapsedKey(projectSlug))
  // Default to collapsed until the user has opened chat in this project.
  if (stored === null) return true
  return stored === '1'
}

export function persistSidebarCollapsed(
  projectSlug: string,
  collapsed: boolean,
) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(
    sidebarCollapsedKey(projectSlug),
    collapsed ? '1' : '0',
  )
}
